import { createContext, useContext } from "react";
import { useCrud } from "../BackendProvider";
import _ from "lodash";
import { useRoles } from "../user/Auth";
import { useQueryClient } from "@tanstack/react-query";
import { useProductMapping } from "src/products/products";
import { Member } from "src/patents/patents";
import { does_patent_cover_income } from ".";
import { useSingleTeamMangement } from "src/Management";
import { useFxContext } from "src/forecast/FxProvider";

export type Income = {
    incomeId?: number,
    commodityId: number,
    countryCode: string,
    amount: number,
    currency: string,
    year: number,
}

/** commodityId -> countryCode -> year -> Income */
export type IncomeLookup = Record<number, Record<string, Record<number, Income>>>

const IncomeContext = createContext({
    incomes: [] as Income[],
    postIncome: (income: Income) => Promise.resolve({}),
    deleteIncome: (income: Income) => Promise.resolve({}),

    incomeLookup: {} as IncomeLookup,
    years: [] as number[],
    isLoading: false as boolean,
    reload: () => {},
});

// const initial_incomes = [
//     { incomeId: 1, commodityId: 92, countryCode: 'US', amount: 100, year: 2020 },
//     { incomeId: 2, commodityId: 92, countryCode: 'US', amount: 200, year: 2021 },
//     { incomeId: 3, commodityId: 92, countryCode: 'US', amount: 300, year: 2022 },
//     { incomeId: 4, commodityId: 92, countryCode: 'CH', amount: 1000, year: 2021 },
//     { incomeId: 5, commodityId: 92, countryCode: 'CH', amount: 2000, year: 2022 },
// ]

export function IncomeProvider({children}: {children: React.ReactNode}) {
    const client = useQueryClient()
    //const {data: incomes, isLoading, postMutation: postIncome, deleteMutation: deleteIncome} = useDummyCrud<Income>('income', 'incomeId', initial_incomes)
    const {hasRevenues} = useRoles()
    const {data: incomes, isLoading, postMutation: postIncome, deleteMutation: deleteIncome} = useCrud<Income>('income', i => i.incomeId, hasRevenues)

    // console.log({incomes})

    const incomeLookup = _(incomes)
        .groupBy('commodityId')
        .mapValues(_is => _(_is)
            .groupBy('countryCode')
            .mapValues(byCc => _.keyBy(byCc, c => c.year))
            .value()
        )
        .value()
    const years = _(incomes).map(i => i.year).uniq().sortBy(y => y).value();
    const reload = () => client.invalidateQueries({queryKey: ['income']})

    const value = {
        incomes, postIncome, deleteIncome,
        incomeLookup,
        years,
        isLoading, reload,
    }

    return (
        <IncomeContext.Provider {...{value}}>
            {children}
        </IncomeContext.Provider>
    )
}

export function useIncome() {
    return useContext(IncomeContext)
}

export function useProtectedIncome({year}: {year: number}) {
    const {hasClaimScopes} = useRoles()
    const {team} = useSingleTeamMangement()
    const ccy = team?.currency ?? 'EUR'
    const {fxConverter} = useFxContext()
    const {years, incomeLookup} = useIncome()
    
    const { commodities, membersByCommodityId, claimScopeMembersByCommodityId: _claimScopeMembersByCommodityId } = useProductMapping()
    const claimScopeMembersByCommodityId = hasClaimScopes ? _claimScopeMembersByCommodityId : {}
    // TODO: we should go back in time to determine correct family member status


    let total = 0.0
    let protected_income = 0.0
    let pending_income = 0.0
    
    const doOverview = true
    const overviewTable = []

    _(commodities).forEach(({commodityId, commodityReference, commodityClass}) => {
        const [granted, pending] = _([
            ...membersByCommodityId[commodityId] ?? [],
            ...claimScopeMembersByCommodityId[commodityId] ?? [],
        ]).filter((m: Member) => m.familyMemberStatus === 'granted' || m.familyMemberStatus === 'pending')
            .partition(m => m.familyMemberStatus === 'granted')
            .value()

        // console.log({commodityId, granted, pending, incomeLookup})
        const perCommodity = incomeLookup[commodityId] ?? {}
        // console.log({perCommodity})
        _(perCommodity).toPairs().forEach(([countryCode, byYear]) => {
            const income = byYear[year]
            // console.log({countryCode, income})
            if (income) {
                const incomeAmount = fxConverter.convert(income.amount, income.currency, ccy)
                total += incomeAmount
                if (granted.some(m => does_patent_cover_income(m, countryCode))) {
                  protected_income += incomeAmount
                } else if (pending.some(m => does_patent_cover_income(m, countryCode))) {
                  pending_income += incomeAmount
                }

                if (doOverview) {
                    const row = {
                        commodityId, commodityClass, commodityReference, countryCode, year, amount: income.amount, currency: income.currency, incomeAmount,  
                        protecting: granted.filter(m => does_patent_cover_income(m, countryCode)), pending: pending.filter(m => does_patent_cover_income(m, countryCode))
                    }
                    overviewTable.push(row)
                }
            }
        })
    })

    return { total, protected_income, pending_income, years, overviewTable}
}